<template>
  <section class="bg-[#393939] product-gallary" id="gallery">
    <div class="container py-[60px] md:py-[100px] gallery">
      <h1
        class="text-[20px] lg:text-[28px] text-white font-semibold text-center font-saira leading-[28px] lg:leading-[36px] uppercase break-words px-4 md:py-0 py-6"
        v-text="$t('common.gallery')"
      />
      <div class="mt-8 viewer lg:mt-9">
        <div
          v-for="(item, index) in media"
          :key="index"
          class="flex"
          :class="
            index % 2 == 0
              ? 'flex-col lg:flex-row-reverse'
              : 'flex-col lg:flex-row'
          "
        >
          <div
            class="flex w-full"
            :class="[
              chunkItem.length == 2 && item.length == 1
                ? 'flex-row'
                : 'flex-row lg:flex-col',
              chunkItem.length == 1 && item.length == 1
                ? ' flex-1 '
                : index2 == 1
                ? ' flex-[0.8]'
                : 'flex-[1.2]',
            ]"
            v-for="(chunkItem, index2) in item"
            :key="index2"
          >
            <div
              class="w-full p-[5px] group"
              :class="
                chunkItem.length == 1 && item.length == 1
                  ? 'h-[168px] md:h-[330px] lg:h-[610px]'
                  : index2 == 1
                  ? 'h-[168px] md:h-[330px] lg:h-[660px]'
                  : 'h-[168px] md:h-[330px]'
              "
              v-for="(galleryItem, index3) in chunkItem"
              :key="index3"
            >
              <div
                class="relative w-full h-full cursor-pointer group-hover:border-2 group-hover:border-primary"
                v-if="galleryItem.mime_type.includes('image')"
                @click.prevent="openLightbox(galleryItem.index, false)"
              >
                <ImgLazyLoader
                  :src="galleryItem.url_original"
                  alt="Gallery Image"
                  class="w-full h-full"
                  image-class="object-cover object-center w-full h-full "
                />
                <div
                  class="absolute top-0 left-0 z-10 items-center justify-center hidden w-full h-full group-hover:flex"
                >
                  <SearchIcon class="w-8 h-8 text-white" />
                </div>
              </div>
              <div
                v-else
                class="relative w-full h-full cursor-pointer group-hover:border-2 group-hover:border-primary"
                @click.prevent="openLightbox(galleryItem.index, true)"
              >
                <ImgLazyLoader
                  :src="galleryItem.url_md"
                  alt="Gallery Image"
                  class="w-full h-full"
                  image-class="object-cover object-center w-full h-full"
                />
                <div
                  v-if="chunkItem.length == 1 && item.length == 1"
                  class="absolute top-0 left-0 z-10 flex items-center justify-center w-full h-full"
                >
                  <PlayIcon
                    class="w-16 h-16 lg:w-[100px] lg:h-[100px] text-white rounded-full"
                    style="backdrop-filter: blur(17.5px)"
                  />
                </div>
                <div
                  v-else
                  class="absolute bg-opacity-40 rounded-full top-2 left-2 z-10 items-center justify-center h-[28px] md:h-[50px] p-[10px] md:p-[13px] backdrop-filter bg-black backdrop-blur-[17.5px] flex group-hover:bg-white group-hover:backdrop-none"
                >
                  <PlayMiddleIcon
                    class="h-4 w-4 md:w-[26px] md:h-[26px] text-white group-hover:text-black rounded-full"
                  />
                  <span
                    class="ml-1 text-sm font-semibold text-white md:ml-2 md:text-base lg:text-lg font-saira group-hover:text-black"
                    v-text="$t('common.watch')"
                  ></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!isMobile || portrait">
        <Lightbox
          :items="mediaLightBox"
          :initialIndex="lightboxIndex"
          @update:visible="(e) => (lightboxIndex = e)"
        />
      </div>
      <div v-if="hasMore" class="flex items-center w-full lg:mt-[60px] mt-5">
        <span class="w-full border-t border-white border-opacity-15"></span>
        <span
          v-if="isMore"
          class="bg-[#4E4E4E] text-white uppercase font-saira lg:text-[18px] lg:leading-[26px] rounded-full px-5 py-2 whitespace-nowrap cursor-pointer"
          v-text="$t('common.see_more')"
          @click="() => loadMore(true)"
        ></span>
        <span
          v-else
          class="bg-[#4E4E4E] text-white uppercase font-saira lg:text-[18px] lg:leading-[26px] rounded-full px-5 py-2 whitespace-nowrap cursor-pointer"
          v-text="$t('common.see_less')"
          @click="() => loadMore(false)"
        ></span>
        <span class="w-full border-t border-white border-opacity-15"></span>
      </div>
    </div>
    <ProductMobileGallery
      v-if="isMobile && !portrait"
      :gallery="props.gallery"
      :index="lightboxIndex"
      @index="(index) => (lightboxIndex = index)"
      @handle-controller="handleController"
    />
  </section>
</template>

<script setup>
import { chunk } from "lodash-es"; // Import only the function you need

const SearchIcon = defineAsyncComponent(() =>
  import("~/assets/svg/heroicons/search.svg?component")
);
const PlayMiddleIcon = defineAsyncComponent(() =>
  import("~/assets/svg/heroicons/play-middle.svg?component")
);
const PlayIcon = defineAsyncComponent(() =>
  import("~/assets/svg/heroicons/play.svg?component")
);

const props = defineProps({
  gallery: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits();

const selectedIndex = ref(0);
const windowWidth = ref(0);
const lightboxIndex = ref(null);
const media = ref([]);
const mediaLightBox = ref([]);
const isVideo = ref(false);
const isMore = ref(false);
const hasMore = ref(false);
const chunkItems = ref([]);
const currentIndex = ref(0);
const isMobile = ref(false);
const portrait = ref(false);
const lightbox = ref(null);

onMounted(async () => {
  if (!process.browser) return false;

  screen.orientation.addEventListener("change", onChangeScreen);

  let items = props.gallery.map((_v, _i) => {
    return { ..._v, index: _i };
  });

  let firstItem = items[0];

  let remainingItem = items.splice(1, items.length);

  mediaLightBox.value.push({
    src: firstItem.url_original,
    poster: firstItem.url_md,
    type: firstItem.mime_type,
  });

  if (remainingItem.length) {
    chunkItems.value = chunk(remainingItem, 6);

    if (chunkItems.value.length > 1) {
      hasMore.value = true;
      isMore.value = true;
    }
    chunkItems.value[currentIndex.value].forEach((arrayItem) => {
      mediaLightBox.value.push({
        src: arrayItem.url_original,
        poster: arrayItem.url_md,
        type: arrayItem.mime_type,
      });
    });
  }

  setMedia(firstItem);

  emit("success");

  if (process.browser) {
    windowWidth.value = window.innerWidth;
    if (windowWidth.value <= 767) {
      isMobile.value = true;
    } else {
      isMobile.value = false;
    }
    await nextTick();
    window.addEventListener("resize", onResize);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onResize);
  window.removeEventListener("change", onChangeScreen);
});

function handleController(controller) {
  if (controller.value) {
    controller.value.abort();
    controller.value = null;
  }
}
function onChangeScreen() {
  portrait.value = !screen.orientation.type.includes("portrait");
}

function openLightbox(index, hasVideo) {
  lightboxIndex.value = index;
  isVideo.value = hasVideo;
}

function removeMedia() {
  media.value.splice(3, media.value.length - 1);
}

function setMedia(firstItem = null) {
  let doubleChunkItem = [];
  if (chunkItems.value.length) {
    const mediaData = chunk(chunkItems.value[currentIndex.value], 3);

    mediaData.forEach((arrItem) => {
      doubleChunkItem.push(chunk(arrItem, 2));
    });

    if (firstItem) {
      media.value = [[[firstItem]], ...doubleChunkItem];
    } else {
      media.value = [...media.value, ...doubleChunkItem];
    }
  } else if (firstItem) {
    media.value = [[[firstItem]]];
  }
}

function loadMore(isLoadMore) {
  if (isLoadMore && chunkItems.value.length > 1) {
    currentIndex.value += 1;
    chunkItems.value[currentIndex.value].forEach(async (arrayItem) => {
      mediaLightBox.value.push({
        src: arrayItem.url_original,
        poster: arrayItem.url_md,
        type: arrayItem.mime_type,
      });
    });
    if (chunkItems.value.length - 1 == currentIndex.value) {
      isMore.value = false;
    }
    setMedia();
  } else {
    currentIndex.value = 0;

    mediaLightBox.value.splice(
      chunkItems.value[currentIndex.value].length + 1,
      mediaLightBox.value.length - 1
    );

    isMore.value = true;

    removeMedia();
  }
}

function onResize() {
  if (
    selectedIndex.value !== null &&
    selectedIndex.value >= 0 &&
    isMobile.value
  ) {
    return false;
  }
  windowWidth.value = window.innerWidth;
  if (windowWidth.value <= 767) {
    isMobile.value = true;
    lightboxIndex.value = null;
  } else {
    isMobile.value = false;
  }
}
</script>
<style lang="postcss">
.cool-lightbox {
  @apply !z-[99999999999];
}
.vue-lb-content {
  @apply max-w-none mb-0;
}
.vue-lb-header {
  @apply absolute z-10 right-2;
}
</style>
